import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { getData } from "../../../services/FetchNodeservices";
import { MdAccessTime } from "react-icons/md";
import { FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";

export default function AllUserAttendance() {
  const user = useSelector((state) => state.user);

  // Allowed admin IDs
  const allowedAdmins = [5, 59];

  // Check access
  const adminData = JSON.parse(localStorage.getItem("ADMIN"));
  const hasAccess =
    adminData || (user && allowedAdmins.includes(Number(user.id)));

  const [attendanceList, setAttendanceList] = useState([]);

  const [filterType, setFilterType] = useState("today");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  useEffect(() => {
    if (!hasAccess) return;

    const fetchAttendance = async () => {
      const res = await getData("attendances/all");
      if (res.status) setAttendanceList(res.data);
    };

    fetchAttendance();
  }, [hasAccess]);

  // 📅 Date Calculations
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const lastWeek = new Date(today);
  lastWeek.setDate(today.getDate() - 7);

  const lastMonth = new Date(today);
  lastMonth.setDate(today.getDate() - 30);

  // 🔍 Apply Filters
  const filtered = useMemo(() => {
    let data = [...attendanceList];

    let fromDate, toDate;

    switch (filterType) {
      case "today":
        fromDate = new Date(new Date().setHours(0, 0, 0));
        toDate = new Date();
        break;

      case "yesterday":
        fromDate = new Date(yesterday.setHours(0, 0, 0));
        toDate = new Date(yesterday.setHours(23, 59, 59));
        break;

      case "last7":
        fromDate = lastWeek;
        toDate = new Date();
        break;

      case "last30":
        fromDate = lastMonth;
        toDate = new Date();
        break;

      case "custom":
        if (customFrom && customTo) {
          fromDate = new Date(customFrom + "T00:00:00");
          toDate = new Date(customTo + "T23:59:59");
        }
        break;
    }

    if (fromDate && toDate) {
      data = data.filter((row) => {
        const t = new Date(row.punch_in_time);
        return t >= fromDate && t <= toDate;
      });
    }

    return data;
  }, [attendanceList, filterType, customFrom, customTo]);

  // 📌 Group by Date
  const grouped = useMemo(() => {
    const groups = {};

    filtered.forEach((row) => {
      const dateKey = new Date(row.punch_in_time)
        .toISOString()
        .split("T")[0];

      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(row);
    });

    return Object.keys(groups)
      .sort((a, b) => new Date(b) - new Date(a))
      .reduce((acc, key) => ({ ...acc, [key]: groups[key] }), {});
  }, [filtered]);

  // 📤 Export to Excel
  const exportExcel = () => {
    let exportData = [];

    Object.keys(grouped).forEach((date) => {
      grouped[date].forEach((row) => {
        exportData.push({
          Date: date,
          Name: `${row.first_name} ${row.last_name}`,
          PunchIn: row.punch_in_time,
          PunchOut: row.punch_out_time || "Working",
          Duration: row.duration || "---",
        });
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
    XLSX.writeFile(workbook, "Attendance_Report.xlsx");
  };

  // ❌ Access Denied UI
  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-screen text-red-600 text-2xl font-bold">
        🚫 Access Denied
      </div>
    );
  }

  return (
    <div className="p-6 mt-12">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold mb-6 text-gray-800">
          Attendance Dashboard
        </h1>

        {/* Export Button */}
        <button
          onClick={exportExcel}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow-md"
        >
          <FaFileExcel size={20} />
          Export Excel
        </button>
      </div>

      {/* FILTERS */}
      <div className="bg-white p-5 rounded-xl shadow-md border flex flex-wrap items-center gap-4">
        {/* Date Filter */}
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-600">
            Date Filter
          </label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="p-2 border rounded-lg bg-gray-50"
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 Days</option>
            <option value="last30">Last 30 Days</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>

        {/* Custom From */}
        {filterType === "custom" && (
          <>
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-600">
                From
              </label>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="p-2 border rounded-lg bg-gray-50"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-600">
                To
              </label>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="p-2 border rounded-lg bg-gray-50"
              />
            </div>
          </>
        )}
      </div>

      {/* GROUPED OUTPUT */}
      {Object.keys(grouped).length === 0 && (
        <p className="mt-6 text-gray-600 text-center text-lg">
          No attendance found for selected filters.
        </p>
      )}

      {Object.keys(grouped).map((date) => (
        <div
          key={date}
          className="mt-6 bg-white p-5 rounded-xl shadow border border-gray-200"
        >
          <h2 className="text-xl font-bold mb-3 text-blue-700">{date}</h2>

          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Punch In</th>
                <th className="p-3 text-left">Punch Out</th>
                <th className="p-3 text-left">Duration</th>
              </tr>
            </thead>

            <tbody>
              {grouped[date].map((row) => (
                <tr
                  key={row.id}
                  className="border-t hover:bg-gray-50 transition"
                >
                  <td className="p-3 font-medium">
                    {row.first_name} {row.last_name}
                  </td>

                  <td className="p-3">
                    {new Date(row.punch_in_time).toLocaleString()}
                  </td>

                  <td className="p-3">
                    {row.punch_out_time ? (
                      new Date(row.punch_out_time).toLocaleString()
                    ) : (
                      <span className="text-yellow-600 flex items-center gap-1">
                        <MdAccessTime /> Working
                      </span>
                    )}
                  </td>

                  <td className="p-3 font-semibold">
                    {row.duration || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
